import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useCandidateForm } from "../CandidateFormContext";
import { toast } from "react-toastify";

/**
 * CandidatePersonalSubTabs - A validation-aware navigation component for Personal sub-tabs
 *
 * In create mode (isCreating=true):
 * - Basic is always accessible
 * - Professional is locked until Basic is completed
 * - Profile is locked until Professional is completed
 * - Emergency Contacts is locked until Profile is completed
 *
 * In edit mode (isCreating=false):
 * - All tabs are accessible freely
 */
const CandidatePersonalSubTabs = ({ name, link }) => {
  const { isCreating, canAccessTab, completedTabs } = useCandidateForm();
  const location = useLocation();

  // Check if we're at the index route (/personal without a sub-path)
  // In that case, "basic" should appear active since the index route renders Basic
  const isIndexRoute = location.pathname.endsWith('/personal') || location.pathname.endsWith('/personal/');
  const isBasicOnIndex = link === 'basic' && isIndexRoute;

  const isAccessible = canAccessTab(link);

  // Determine completion status for visual indicator
  const isCompleted = completedTabs.includes(link);

  const handleClick = (e) => {
    if (isCreating && !isAccessible) {
      e.preventDefault();

      // Provide specific message based on which tab is locked
      const tabMessages = {
        'professional': 'Please complete and save Basic information first',
        'profile': 'Please complete and save Professional information first',
        'emergency-contacts': 'Please complete and save Profile information first',
      };

      toast.warning(tabMessages[link] || 'Please complete the previous tab first', {
        toastId: "tab-validation-warning",
      });
      return;
    }
  };

  // Base styles
  const baseStyles = "p-1 flex px-4 py-2 justify-start items-start gap-2 text-xs leading-none transition-all duration-200";

  // Active styles
  const activeStyles = "border-b-2 border-primary-500 text-primary-500 font-bold";

  // Inactive but accessible styles
  const inactiveStyles = "text-text-hint font-normal hover:text-primary-400";

  // Disabled/locked styles
  const disabledStyles = "text-gray-400 cursor-not-allowed opacity-50";

  return (
    <NavLink
      to={link}
      onClick={handleClick}
      className={({ isActive }) => {
        if (isCreating && !isAccessible) {
          return `${baseStyles} ${disabledStyles}`;
        }
        return (isActive || isBasicOnIndex)
          ? `${baseStyles} ${activeStyles}`
          : `${baseStyles} ${inactiveStyles}`;
      }}
    >
      {/* Show checkmark for completed tabs in create mode */}
      {isCreating && isCompleted && (
        <span className="text-green-500 text-xs mr-1">&#10003;</span>
      )}
      {name}
      {/* Show lock icon for inaccessible tabs in create mode */}
      {isCreating && !isAccessible && (
        <span className="text-gray-400 text-xs ml-1">&#128274;</span>
      )}
    </NavLink>
  );
};

export default CandidatePersonalSubTabs;
